import React from 'react';
import { COLORS, DIMENSIONS } from '../utils/constants';
import { ExportMenu } from './ExportMenu';

interface HeaderBarProps {
  onPrint: () => void;
  onExportCutListCsv: () => void;
  onExportBomCsv: () => void;
  onExportJson: () => void;
  onImport: () => void;
  onClearAll: () => void;
  onToggleBackground: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

export const HeaderBar: React.FC<HeaderBarProps> = ({
  onPrint,
  onExportCutListCsv,
  onExportBomCsv,
  onExportJson,
  onImport,
  onClearAll,
  onToggleBackground,
  onUndo,
  onRedo,
  canUndo,
  canRedo
}) => {
  const buttonStyle = {
    padding: '8px 16px',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'background-color 0.2s'
  };

  const historyButtonStyle = (enabled: boolean) => ({
    ...buttonStyle,
    backgroundColor: '#636e72',
    opacity: enabled ? 1 : 0.4,
    cursor: enabled ? 'pointer' : 'not-allowed'
  });

  return (
    <div style={{
      height: `${DIMENSIONS.HEADER_HEIGHT}px`,
      backgroundColor: COLORS.HEADER,
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 20px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      zIndex: 1000
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <img
          src={`${process.env.PUBLIC_URL}/kiwi-logo.svg`}
          alt="KIWI Van Manufaktur"
          style={{ height: '38px', display: 'block' }}
        />
        <span style={{ fontSize: '20px', fontWeight: 600, color: COLORS.PRIMARY, letterSpacing: '0.5px' }}>
          Harness
        </span>
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={onUndo}
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
          style={historyButtonStyle(canUndo)}
        >
          Undo
        </button>

        <button
          onClick={onRedo}
          disabled={!canRedo}
          title="Redo (Ctrl+Shift+Z)"
          style={historyButtonStyle(canRedo)}
        >
          Redo
        </button>

        <ExportMenu
          buttonStyle={buttonStyle}
          items={[
            {
              label: 'Print / PDF report',
              hint: 'Diagram, cut list, BOM and pinouts',
              onSelect: onPrint
            },
            {
              label: 'Wire cut list (CSV)',
              hint: 'One row per wire with lengths',
              onSelect: onExportCutListCsv
            },
            {
              label: 'Bill of materials (CSV)',
              hint: 'Connector and wire line items',
              onSelect: onExportBomCsv
            },
            {
              label: 'Project backup (JSON)',
              hint: 'Reimportable project file',
              separated: true,
              onSelect: onExportJson
            }
          ]}
        />

        <button
          onClick={onToggleBackground}
          title="Place a reference image behind the diagram"
          style={{
            ...buttonStyle,
            backgroundColor: COLORS.SECONDARY
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = COLORS.SECONDARY_HOVER}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = COLORS.SECONDARY}
        >
          Background
        </button>

        <button
          onClick={onImport}
          style={{
            ...buttonStyle,
            backgroundColor: COLORS.SECONDARY
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = COLORS.SECONDARY_HOVER}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = COLORS.SECONDARY}
        >
          Import
        </button>

        <button
          onClick={onClearAll}
          style={{
            ...buttonStyle,
            backgroundColor: COLORS.DANGER
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = COLORS.DANGER_HOVER}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = COLORS.DANGER}
        >
          Clear All
        </button>
      </div>
    </div>
  );
};